import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { 
  PawPrint, LogOut, Plus, DollarSign, Settings, Heart, Users, BarChart3,
  ClipboardList, Calendar, Baby, ShoppingCart, ListOrdered, Check, X, Edit,
  ArrowRightLeft, Loader2
} from 'lucide-react';

const BreederDashboard = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [litters, setLitters] = useState([]);
  const [animals, setAnimals] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [sales, setSales] = useState([]);
  
  // Dialog states
  const [litterDialogOpen, setLitterDialogOpen] = useState(false);
  const [animalDialogOpen, setAnimalDialogOpen] = useState(false);
  const [waitlistDialogOpen, setWaitlistDialogOpen] = useState(false);
  const [saleDialogOpen, setSaleDialogOpen] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [selectedAnimal, setSelectedAnimal] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Form states
  const [newLitter, setNewLitter] = useState({
    breed: '', species: 'Dog', dam_name: '', dam_registration: '',
    sire_name: '', sire_registration: '', birth_date: '', expected_ready_date: '',
    total_puppies: '', males: '', females: '', price: '', deposit_amount: '', description: ''
  });
  const [newAnimal, setNewAnimal] = useState({
    litter_id: '', name: '', gender: '', color: '', markings: '', microchip_number: '', price: ''
  });
  const [newWaitlistEntry, setNewWaitlistEntry] = useState({
    litter_id: '', breed: '', name: '', email: '', phone: '', gender_preference: 'any',
    color_preference: '', deposit_paid: false, deposit_amount: '', notes: ''
  });
  const [saleData, setSaleData] = useState({
    buyer: { name: '', email: '', phone: '', address: '' },
    price: '', deposit_paid: '', payment_method: '', contract_signed: false, microchip_transferred: false, notes: ''
  });

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, littersRes, animalsRes, waitlistRes, salesRes] = await Promise.all([
        axios.get('/breeder/dashboard').catch(() => ({ data: null })),
        axios.get('/breeder/litters').catch(() => ({ data: [] })),
        axios.get('/breeder/animals').catch(() => ({ data: [] })),
        axios.get('/breeder/waitlist').catch(() => ({ data: [] })),
        axios.get('/breeder/sales').catch(() => ({ data: [] }))
      ]);
      
      setDashboardStats(statsRes.data);
      setLitters(littersRes.data || []);
      setAnimals(animalsRes.data || []);
      setWaitlist(waitlistRes.data || []);
      setSales(salesRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const handleCreateLitter = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/breeder/litters', {
        ...newLitter,
        total_puppies: parseInt(newLitter.total_puppies) || 0,
        males: parseInt(newLitter.males) || 0,
        females: parseInt(newLitter.females) || 0,
        price: parseFloat(newLitter.price) || 0,
        deposit_amount: parseFloat(newLitter.deposit_amount) || 0
      });
      toast.success('Litter created!');
      setLitterDialogOpen(false);
      setNewLitter({
        breed: '', species: 'Dog', dam_name: '', dam_registration: '',
        sire_name: '', sire_registration: '', birth_date: '', expected_ready_date: '',
        total_puppies: '', males: '', females: '', price: '', deposit_amount: '', description: ''
      });
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to create litter');
    }
  };

  const handleAddAnimal = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/breeder/animals', {
        ...newAnimal,
        price: parseFloat(newAnimal.price) || 0
      });
      toast.success('Animal added to litter!');
      setAnimalDialogOpen(false);
      setNewAnimal({ litter_id: '', name: '', gender: '', color: '', markings: '', microchip_number: '', price: '' });
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to add animal');
    }
  };

  const handleAddToWaitlist = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/breeder/waitlist', {
        ...newWaitlistEntry,
        deposit_amount: parseFloat(newWaitlistEntry.deposit_amount) || 0
      });
      toast.success('Added to waitlist!');
      setWaitlistDialogOpen(false);
      setNewWaitlistEntry({
        litter_id: '', breed: '', name: '', email: '', phone: '', gender_preference: 'any',
        color_preference: '', deposit_paid: false, deposit_amount: '', notes: ''
      });
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to add to waitlist');
    }
  };

  const handleSellAnimal = async (e) => {
    e.preventDefault();
    try {
      // First, register the pet in the registry with $1 fee for breeders
      const registerResponse = await axios.post('/pets/enroll', {
        name: selectedAnimal.name,
        species: selectedAnimal.species || 'Dog',
        breed: selectedAnimal.breed || animals.find(a => a.animal_id === selectedAnimal.animal_id)?.litter?.breed,
        color: selectedAnimal.color,
        gender: selectedAnimal.gender,
        date_of_birth: selectedAnimal.date_of_birth,
        microchip_number: selectedAnimal.microchip_number,
        owner_name: saleData.buyer.name,
        owner_email: saleData.buyer.email,
        owner_phone_1: saleData.buyer.phone,
        host_url: window.location.origin
      });
      
      // Mark animal as sold in breeder records
      await axios.put(`/breeder/animals/${selectedAnimal.animal_id}/sell`, {
        buyer: saleData.buyer,
        price: parseFloat(saleData.price) || selectedAnimal.price,
        deposit_paid: parseFloat(saleData.deposit_paid) || 0,
        payment_method: saleData.payment_method,
        contract_signed: saleData.contract_signed,
        microchip_transferred: saleData.microchip_transferred,
        notes: saleData.notes,
        registered_pet_id: registerResponse.data.pet_id
      });
      
      // Redirect to payment for $1 registration
      if (registerResponse.data.payment_url) {
        toast.success('Redirecting to payment for pet registration ($1)...');
        window.location.href = registerResponse.data.payment_url;
      } else {
        toast.success('Animal sold successfully!');
        setSaleDialogOpen(false);
        setSelectedAnimal(null);
        fetchDashboardData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process sale');
    }
  };

  const handleTransferToNewOwner = async (animal) => {
    // Open transfer dialog with buyer info pre-filled
    setSelectedAnimal(animal);
    setSaleData({
      buyer: { name: '', email: '', phone: '', address: '' },
      price: animal.price || '', deposit_paid: '', payment_method: '', 
      contract_signed: false, microchip_transferred: false, notes: ''
    });
    setTransferDialogOpen(true);
  };

  const handleDirectTransfer = async (e) => {
    e.preventDefault();
    if (!selectedAnimal) return;
    
    try {
      // Use the transfer endpoint which handles $20 transfer fee
      const response = await axios.post(`/pets/${selectedAnimal.pet_id || selectedAnimal.animal_id}/transfer`, {
        new_owner_name: saleData.buyer.name,
        new_owner_email: saleData.buyer.email,
        new_owner_phone: saleData.buyer.phone,
        host_url: window.location.origin
      });
      
      if (response.data.payment_url) {
        toast.success('Redirecting to payment for transfer fee ($20)...');
        window.location.href = response.data.payment_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate transfer');
    }
  };

  const handleReserveAnimal = async (animalId, buyerData) => {
    try {
      await axios.put(`/breeder/animals/${animalId}/reserve`, buyerData);
      toast.success('Animal reserved!');
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to reserve animal');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fdfbf7]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fdfbf7]">
      {/* Header */}
      <header data-testid="breeder-dashboard" className="bg-white border-b border-stone-100 shadow-sm">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <PawPrint className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-stone-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {user.business_name || user.name || 'Breeder Dashboard'}
              </h1>
              <p className="text-sm text-stone-600">{user.email}</p>
            </div>
          </div>
          <Button data-testid="logout-btn" onClick={onLogout} variant="outline" className="rounded-full">
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </header>

      <div className="container mx-auto px-6 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white shadow-sm border">
            <TabsTrigger value="overview" className="gap-2"><BarChart3 className="w-4 h-4" />Overview</TabsTrigger>
            <TabsTrigger value="litters" className="gap-2"><Baby className="w-4 h-4" />Litters</TabsTrigger>
            <TabsTrigger value="animals" className="gap-2"><PawPrint className="w-4 h-4" />Animals</TabsTrigger>
            <TabsTrigger value="waitlist" className="gap-2"><ListOrdered className="w-4 h-4" />Waitlist</TabsTrigger>
            <TabsTrigger value="sales" className="gap-2"><ShoppingCart className="w-4 h-4" />Sales</TabsTrigger>
          </TabsList>

          {/* OVERVIEW TAB */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-purple-50 to-white">
                <CardContent className="pt-4 pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-stone-500">Active Litters</p>
                      <p className="text-2xl font-bold text-purple-600">{dashboardStats?.litters?.active || 0}</p>
                    </div>
                    <Baby className="w-8 h-8 text-purple-200" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-green-50 to-white">
                <CardContent className="pt-4 pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-stone-500">Available</p>
                      <p className="text-2xl font-bold text-green-600">{dashboardStats?.animals?.available || 0}</p>
                    </div>
                    <PawPrint className="w-8 h-8 text-green-200" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-orange-50 to-white">
                <CardContent className="pt-4 pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-stone-500">Reserved</p>
                      <p className="text-2xl font-bold text-orange-600">{dashboardStats?.animals?.reserved || 0}</p>
                    </div>
                    <Heart className="w-8 h-8 text-orange-200" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-blue-50 to-white">
                <CardContent className="pt-4 pb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-stone-500">Waitlist</p>
                      <p className="text-2xl font-bold text-blue-600">{dashboardStats?.waitlist?.total || 0}</p>
                    </div>
                    <ListOrdered className="w-8 h-8 text-blue-200" />
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Quick Actions</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-3">
                    <Button onClick={() => setLitterDialogOpen(true)} className="bg-primary hover:bg-primary/90">
                      <Plus className="w-4 h-4 mr-2" />New Litter
                    </Button>
                    <Button variant="outline" onClick={() => setAnimalDialogOpen(true)}>
                      <PawPrint className="w-4 h-4 mr-2" />Add Puppy/Kitten
                    </Button>
                    <Button variant="outline" onClick={() => setWaitlistDialogOpen(true)}>
                      <ListOrdered className="w-4 h-4 mr-2" />Add to Waitlist
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <DollarSign className="w-5 h-5" />Monthly Revenue
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-green-600">${(dashboardStats?.revenue?.monthly || 0).toLocaleString()}</p>
                  <p className="text-sm text-stone-500 mt-1">{dashboardStats?.animals?.sold || 0} animals sold</p>
                </CardContent>
              </Card>
            </div>

            {/* Recent Litters */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Recent Litters</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-4">
                  {litters.slice(0, 3).map((litter) => (
                    <div key={litter.litter_id} className="p-4 border rounded-lg">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-semibold">{litter.breed}</p>
                          <p className="text-sm text-stone-600">{litter.dam_name} x {litter.sire_name}</p>
                        </div>
                        <Badge variant={litter.status === 'active' ? 'default' : 'secondary'}>{litter.status}</Badge>
                      </div>
                      <p className="text-xs text-stone-500">Born: {litter.birth_date}</p>
                      <div className="flex gap-2 mt-2 text-xs">
                        <span className="text-green-600">{litter.available_count || 0} available</span>
                        <span className="text-blue-600">{litter.sold_count || 0} sold</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* LITTERS TAB */}
          <TabsContent value="litters" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">Litter Management</h2>
              <Button onClick={() => setLitterDialogOpen(true)} className="bg-primary hover:bg-primary/90">
                <Plus className="w-4 h-4 mr-2" />New Litter
              </Button>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {litters.map((litter) => (
                <Card key={litter.litter_id}>
                  <CardContent className="pt-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-semibold text-lg">{litter.breed}</h3>
                        <p className="text-sm text-stone-600">{litter.species}</p>
                      </div>
                      <Badge variant={litter.status === 'active' ? 'default' : 'secondary'}>{litter.status}</Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <p><strong>Dam:</strong> {litter.dam_name}</p>
                      <p><strong>Sire:</strong> {litter.sire_name}</p>
                      <p><strong>Born:</strong> {litter.birth_date}</p>
                      <p><strong>Ready:</strong> {litter.expected_ready_date || 'TBD'}</p>
                      <p><strong>Total:</strong> {litter.total_puppies} ({litter.males}M/{litter.females}F)</p>
                      <p><strong>Price:</strong> ${litter.price?.toLocaleString()}</p>
                    </div>
                    <div className="flex gap-2 mt-3 pt-3 border-t">
                      <Badge variant="outline" className="text-green-600">{litter.available_count || 0} available</Badge>
                      <Badge variant="outline" className="text-blue-600">{litter.sold_count || 0} sold</Badge>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-3 w-full"
                      onClick={() => { setNewAnimal({...newAnimal, litter_id: litter.litter_id}); setAnimalDialogOpen(true); }}
                    >
                      <Plus className="w-4 h-4 mr-1" />Add Puppy/Kitten
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>

            {litters.length === 0 && (
              <div className="text-center py-12">
                <Baby className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                <p className="text-stone-500">No litters yet</p>
                <Button onClick={() => setLitterDialogOpen(true)} className="mt-4">Create Your First Litter</Button>
              </div>
            )}
          </TabsContent>

          {/* ANIMALS TAB */}
          <TabsContent value="animals" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">Animals ({animals.length})</h2>
              <Button onClick={() => setAnimalDialogOpen(true)} className="bg-primary hover:bg-primary/90">
                <Plus className="w-4 h-4 mr-2" />Add Animal
              </Button>
            </div>

            <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
              {animals.map((animal) => (
                <Card key={animal.animal_id} className="hover:shadow-md transition-shadow">
                  <CardContent className="pt-4">
                    <div className="aspect-square bg-stone-100 rounded-lg mb-3 flex items-center justify-center">
                      {animal.photos?.length > 0 ? (
                        <img src={animal.photos[0]} alt={animal.name} className="w-full h-full object-cover rounded-lg" />
                      ) : (
                        <PawPrint className="w-12 h-12 text-stone-300" />
                      )}
                    </div>
                    <div className="space-y-1">
                      <div className="flex justify-between items-start">
                        <h3 className="font-semibold">{animal.name || 'Unnamed'}</h3>
                        <Badge variant={
                          animal.status === 'available' ? 'default' :
                          animal.status === 'reserved' ? 'secondary' :
                          animal.status === 'sold' ? 'outline' : 'destructive'
                        }>
                          {animal.status}
                        </Badge>
                      </div>
                      <p className="text-sm text-stone-600">{animal.gender} • {animal.color}</p>
                      <p className="text-sm font-semibold">${animal.price?.toLocaleString()}</p>
                      {animal.microchip_number && (
                        <p className="text-xs text-stone-400">Chip: {animal.microchip_number}</p>
                      )}
                    </div>
                    {animal.status === 'available' && (
                      <div className="flex gap-2 mt-3">
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="flex-1"
                          onClick={() => handleReserveAnimal(animal.animal_id, { name: 'Pending' })}
                        >
                          Reserve
                        </Button>
                        <Button 
                          size="sm" 
                          className="flex-1 bg-green-600 hover:bg-green-700"
                          onClick={() => { setSelectedAnimal(animal); setSaleDialogOpen(true); }}
                        >
                          Sell
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>

            {animals.length === 0 && (
              <div className="text-center py-12">
                <PawPrint className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                <p className="text-stone-500">No animals yet</p>
              </div>
            )}
          </TabsContent>

          {/* WAITLIST TAB */}
          <TabsContent value="waitlist" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">Waitlist ({waitlist.length})</h2>
              <Button onClick={() => setWaitlistDialogOpen(true)} className="bg-primary hover:bg-primary/90">
                <Plus className="w-4 h-4 mr-2" />Add to Waitlist
              </Button>
            </div>

            <Card>
              <CardContent className="pt-4">
                {waitlist.length > 0 ? (
                  <div className="space-y-3">
                    {waitlist.map((entry, idx) => (
                      <div key={entry.waitlist_id} className="flex items-center justify-between p-4 border rounded-lg">
                        <div className="flex items-center gap-4">
                          <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center font-bold text-primary">
                            {entry.position || idx + 1}
                          </div>
                          <div>
                            <p className="font-semibold">{entry.name}</p>
                            <p className="text-sm text-stone-600">{entry.email} • {entry.phone}</p>
                            <div className="flex gap-2 mt-1">
                              <Badge variant="outline" className="text-xs">{entry.breed || 'Any breed'}</Badge>
                              <Badge variant="outline" className="text-xs">{entry.gender_preference}</Badge>
                              {entry.deposit_paid && <Badge className="text-xs bg-green-600">Deposit Paid</Badge>}
                            </div>
                          </div>
                        </div>
                        <Badge variant={entry.status === 'waiting' ? 'default' : 'secondary'}>{entry.status}</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-stone-500 py-8">No one on the waitlist yet</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* SALES TAB */}
          <TabsContent value="sales" className="space-y-4">
            <h2 className="text-xl font-semibold">Sales History</h2>

            <div className="grid md:grid-cols-3 gap-4 mb-6">
              <Card className="bg-green-50">
                <CardContent className="pt-4">
                  <p className="text-sm text-stone-600">Total Sales</p>
                  <p className="text-2xl font-bold text-green-600">{sales.length}</p>
                </CardContent>
              </Card>
              <Card className="bg-blue-50">
                <CardContent className="pt-4">
                  <p className="text-sm text-stone-600">Total Revenue</p>
                  <p className="text-2xl font-bold text-blue-600">
                    ${sales.reduce((sum, s) => sum + (s.price || 0), 0).toLocaleString()}
                  </p>
                </CardContent>
              </Card>
              <Card className="bg-purple-50">
                <CardContent className="pt-4">
                  <p className="text-sm text-stone-600">Avg Price</p>
                  <p className="text-2xl font-bold text-purple-600">
                    ${sales.length > 0 ? Math.round(sales.reduce((sum, s) => sum + (s.price || 0), 0) / sales.length).toLocaleString() : 0}
                  </p>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardContent className="pt-4">
                {sales.length > 0 ? (
                  <div className="space-y-3">
                    {sales.map((sale) => (
                      <div key={sale.sale_id} className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <p className="font-semibold">{sale.animal?.name || 'Animal'}</p>
                          <p className="text-sm text-stone-600">
                            Sold to: {sale.buyer?.name || 'N/A'} • {sale.buyer?.email}
                          </p>
                          <p className="text-xs text-stone-400">{new Date(sale.sale_date).toLocaleDateString()}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-green-600">${sale.price?.toLocaleString()}</p>
                          {sale.contract_signed && <Badge className="text-xs">Contract Signed</Badge>}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-stone-500 py-8">No sales yet</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* CREATE LITTER DIALOG */}
      <Dialog open={litterDialogOpen} onOpenChange={setLitterDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Litter</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateLitter} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Breed *</Label>
                <Input value={newLitter.breed} onChange={(e) => setNewLitter({...newLitter, breed: e.target.value})} required />
              </div>
              <div>
                <Label>Species</Label>
                <Select value={newLitter.species} onValueChange={(v) => setNewLitter({...newLitter, species: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Dog">Dog</SelectItem>
                    <SelectItem value="Cat">Cat</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Dam (Mother) Name *</Label>
                <Input value={newLitter.dam_name} onChange={(e) => setNewLitter({...newLitter, dam_name: e.target.value})} required />
              </div>
              <div>
                <Label>Dam Registration #</Label>
                <Input value={newLitter.dam_registration} onChange={(e) => setNewLitter({...newLitter, dam_registration: e.target.value})} />
              </div>
              <div>
                <Label>Sire (Father) Name *</Label>
                <Input value={newLitter.sire_name} onChange={(e) => setNewLitter({...newLitter, sire_name: e.target.value})} required />
              </div>
              <div>
                <Label>Sire Registration #</Label>
                <Input value={newLitter.sire_registration} onChange={(e) => setNewLitter({...newLitter, sire_registration: e.target.value})} />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Birth Date</Label>
                <Input type="date" value={newLitter.birth_date} onChange={(e) => setNewLitter({...newLitter, birth_date: e.target.value})} />
              </div>
              <div>
                <Label>Expected Ready Date</Label>
                <Input type="date" value={newLitter.expected_ready_date} onChange={(e) => setNewLitter({...newLitter, expected_ready_date: e.target.value})} />
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label>Total Puppies/Kittens</Label>
                <Input type="number" value={newLitter.total_puppies} onChange={(e) => setNewLitter({...newLitter, total_puppies: e.target.value})} />
              </div>
              <div>
                <Label>Males</Label>
                <Input type="number" value={newLitter.males} onChange={(e) => setNewLitter({...newLitter, males: e.target.value})} />
              </div>
              <div>
                <Label>Females</Label>
                <Input type="number" value={newLitter.females} onChange={(e) => setNewLitter({...newLitter, females: e.target.value})} />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Price per Animal ($)</Label>
                <Input type="number" value={newLitter.price} onChange={(e) => setNewLitter({...newLitter, price: e.target.value})} />
              </div>
              <div>
                <Label>Deposit Amount ($)</Label>
                <Input type="number" value={newLitter.deposit_amount} onChange={(e) => setNewLitter({...newLitter, deposit_amount: e.target.value})} />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={newLitter.description} onChange={(e) => setNewLitter({...newLitter, description: e.target.value})} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setLitterDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-primary hover:bg-primary/90">Create Litter</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ADD ANIMAL DIALOG */}
      <Dialog open={animalDialogOpen} onOpenChange={setAnimalDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Puppy/Kitten</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddAnimal} className="space-y-4">
            <div>
              <Label>Litter *</Label>
              <Select value={newAnimal.litter_id} onValueChange={(v) => setNewAnimal({...newAnimal, litter_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select litter" /></SelectTrigger>
                <SelectContent>
                  {litters.filter(l => l.status === 'active').map(l => (
                    <SelectItem key={l.litter_id} value={l.litter_id}>{l.breed} - {l.dam_name} x {l.sire_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name</Label>
                <Input value={newAnimal.name} onChange={(e) => setNewAnimal({...newAnimal, name: e.target.value})} />
              </div>
              <div>
                <Label>Gender *</Label>
                <Select value={newAnimal.gender} onValueChange={(v) => setNewAnimal({...newAnimal, gender: v})}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Male">Male</SelectItem>
                    <SelectItem value="Female">Female</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Color</Label>
                <Input value={newAnimal.color} onChange={(e) => setNewAnimal({...newAnimal, color: e.target.value})} />
              </div>
              <div>
                <Label>Markings</Label>
                <Input value={newAnimal.markings} onChange={(e) => setNewAnimal({...newAnimal, markings: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Microchip Number</Label>
                <Input value={newAnimal.microchip_number} onChange={(e) => setNewAnimal({...newAnimal, microchip_number: e.target.value})} />
              </div>
              <div>
                <Label>Price ($)</Label>
                <Input type="number" value={newAnimal.price} onChange={(e) => setNewAnimal({...newAnimal, price: e.target.value})} />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAnimalDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-primary hover:bg-primary/90">Add Animal</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* WAITLIST DIALOG */}
      <Dialog open={waitlistDialogOpen} onOpenChange={setWaitlistDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add to Waitlist</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddToWaitlist} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name *</Label>
                <Input value={newWaitlistEntry.name} onChange={(e) => setNewWaitlistEntry({...newWaitlistEntry, name: e.target.value})} required />
              </div>
              <div>
                <Label>Breed Preference</Label>
                <Input value={newWaitlistEntry.breed} onChange={(e) => setNewWaitlistEntry({...newWaitlistEntry, breed: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email *</Label>
                <Input type="email" value={newWaitlistEntry.email} onChange={(e) => setNewWaitlistEntry({...newWaitlistEntry, email: e.target.value})} required />
              </div>
              <div>
                <Label>Phone *</Label>
                <Input value={newWaitlistEntry.phone} onChange={(e) => setNewWaitlistEntry({...newWaitlistEntry, phone: e.target.value})} required />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Gender Preference</Label>
                <Select value={newWaitlistEntry.gender_preference} onValueChange={(v) => setNewWaitlistEntry({...newWaitlistEntry, gender_preference: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Color Preference</Label>
                <Input value={newWaitlistEntry.color_preference} onChange={(e) => setNewWaitlistEntry({...newWaitlistEntry, color_preference: e.target.value})} />
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Checkbox checked={newWaitlistEntry.deposit_paid} onCheckedChange={(c) => setNewWaitlistEntry({...newWaitlistEntry, deposit_paid: c})} />
                <Label className="text-sm">Deposit Paid</Label>
              </div>
              {newWaitlistEntry.deposit_paid && (
                <div className="flex-1">
                  <Input type="number" placeholder="Deposit amount" value={newWaitlistEntry.deposit_amount} onChange={(e) => setNewWaitlistEntry({...newWaitlistEntry, deposit_amount: e.target.value})} />
                </div>
              )}
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={newWaitlistEntry.notes} onChange={(e) => setNewWaitlistEntry({...newWaitlistEntry, notes: e.target.value})} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setWaitlistDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-primary hover:bg-primary/90">Add to Waitlist</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* SALE DIALOG */}
      <Dialog open={saleDialogOpen} onOpenChange={setSaleDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Complete Sale & Register Pet</DialogTitle>
          </DialogHeader>
          {selectedAnimal && (
            <form onSubmit={handleSellAnimal} className="space-y-4">
              <div className="p-3 bg-stone-50 rounded-lg">
                <p className="font-semibold">{selectedAnimal.name || 'Animal'}</p>
                <p className="text-sm text-stone-600">{selectedAnimal.gender} • {selectedAnimal.color}</p>
                <p className="text-lg font-bold text-green-600">${selectedAnimal.price?.toLocaleString()}</p>
              </div>
              
              <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-800">
                <strong>Pet Registration Fee: $1.00</strong>
                <p className="text-xs mt-1">This registers the pet in the International Pet Registry with the new owner's information.</p>
              </div>
              
              <div className="space-y-3">
                <h4 className="font-semibold">New Owner Information</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Name *</Label>
                    <Input value={saleData.buyer.name} onChange={(e) => setSaleData({...saleData, buyer: {...saleData.buyer, name: e.target.value}})} required />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input type="email" value={saleData.buyer.email} onChange={(e) => setSaleData({...saleData, buyer: {...saleData.buyer, email: e.target.value}})} required />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input value={saleData.buyer.phone} onChange={(e) => setSaleData({...saleData, buyer: {...saleData.buyer, phone: e.target.value}})} />
                  </div>
                  <div>
                    <Label>Sale Price ($)</Label>
                    <Input type="number" value={saleData.price || selectedAnimal.price} onChange={(e) => setSaleData({...saleData, price: e.target.value})} />
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <Checkbox checked={saleData.contract_signed} onCheckedChange={(c) => setSaleData({...saleData, contract_signed: c})} />
                  <Label className="text-sm">Contract Signed</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox checked={saleData.microchip_transferred} onCheckedChange={(c) => setSaleData({...saleData, microchip_transferred: c})} />
                  <Label className="text-sm">Microchip Transferred</Label>
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setSaleDialogOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={submitting} className="bg-green-600 hover:bg-green-700">
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Complete Sale & Register ($1)
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* TRANSFER OWNERSHIP DIALOG */}
      <Dialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5" />
              Transfer Ownership
            </DialogTitle>
          </DialogHeader>
          {selectedAnimal && (
            <form onSubmit={handleDirectTransfer} className="space-y-4">
              <div className="p-3 bg-stone-50 rounded-lg">
                <p className="font-semibold">{selectedAnimal.name || 'Animal'}</p>
              </div>
              
              <div className="bg-yellow-50 p-3 rounded-lg text-sm text-yellow-800">
                <strong>Transfer Fee: $20.00</strong>
                <p className="text-xs mt-1">New owner will receive login credentials via email if they dont have an account.</p>
              </div>
              
              <div>
                <Label>New Owner Name *</Label>
                <Input 
                  value={saleData.buyer.name} 
                  onChange={(e) => setSaleData({...saleData, buyer: {...saleData.buyer, name: e.target.value}})} 
                  required 
                />
              </div>
              <div>
                <Label>New Owner Email *</Label>
                <Input 
                  type="email" 
                  value={saleData.buyer.email} 
                  onChange={(e) => setSaleData({...saleData, buyer: {...saleData.buyer, email: e.target.value}})} 
                  required 
                />
              </div>
              <div>
                <Label>New Owner Phone</Label>
                <Input 
                  value={saleData.buyer.phone} 
                  onChange={(e) => setSaleData({...saleData, buyer: {...saleData.buyer, phone: e.target.value}})} 
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setTransferDialogOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={submitting}>
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Pay $20 & Transfer
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BreederDashboard;
